import React, { useEffect, useState } from "react";
import { api, formatError } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { Upload, Music, Trash2, FileSpreadsheet, Download } from "lucide-react";

export function AudioUploadRow({ name, onUpdate }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = React.useRef();

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/names/${name.id}/audio`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Audio uploaded for ${name.english}`);
      onUpdate(data);
    } catch (e) { toast.error(formatError(e)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const remove = async () => {
    if (!window.confirm(`Remove audio for ${name.english}?`)) return;
    try {
      await api.delete(`/names/${name.id}/audio`);
      toast.success("Audio removed");
      onUpdate({ ...name, audio_url: null });
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div data-testid={`audio-row-${name.number}`} className="flex items-center gap-4 bg-[#FFFDF9] border border-[#D4AF37]/30 rounded-xl p-4">
      <div className="w-10 h-10 rounded-lg bg-[#4A0E17] text-[#D4AF37] flex items-center justify-center font-mono text-xs">
        #{String(name.number).padStart(4, "0")}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-devanagari text-lg text-[#4A0E17] truncate">{name.sanskrit}</div>
        <div className="text-sm text-[#63534E] truncate">{name.english} — {name.meaning?.slice(0, 60)}…</div>
      </div>
      {name.audio_url ? (
        <>
          <audio data-testid={`audio-preview-${name.number}`} controls src={`${process.env.REACT_APP_BACKEND_URL}${name.audio_url}`} className="h-8 max-w-[220px]" />
          <button data-testid={`remove-audio-${name.number}`} onClick={remove} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-sacred" aria-label="Remove">
            <Trash2 className="w-4 h-4" />
          </button>
        </>
      ) : (
        <>
          <input ref={fileRef} data-testid={`audio-input-${name.number}`} type="file" accept="audio/*" onChange={(e) => upload(e.target.files?.[0])} className="hidden" />
          <Button data-testid={`upload-audio-${name.number}`} disabled={uploading} onClick={() => fileRef.current?.click()} className="bg-[#E65100] hover:bg-[#BF360C] text-white rounded-full h-9 text-xs">
            {uploading ? "Uploading…" : <><Upload className="w-3.5 h-3.5 mr-1.5" /> Upload</>}
          </Button>
        </>
      )}
    </div>
  );
}

export function AudioUploadTab() {
  const [names, setNames] = React.useState([]);
  const [q, setQ] = React.useState("");
  const [onlyMissing, setOnlyMissing] = React.useState(false);

  const load = React.useCallback(() => {
    api.get(`/names?limit=200${q ? `&q=${encodeURIComponent(q)}` : ""}`).then((r) => setNames(r.data.items));
  }, [q]);

  React.useEffect(() => { load(); }, [load]);

  const filtered = onlyMissing ? names.filter((n) => !n.audio_url) : names;

  return (
    <div data-testid="audio-upload-tab">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <Input data-testid="audio-search" placeholder="Search names…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm bg-white border-[#D4AF37]/40 h-10" />
        <label className="flex items-center gap-2 text-sm text-[#4A0E17]">
          <input data-testid="audio-missing-filter" type="checkbox" checked={onlyMissing} onChange={(e) => setOnlyMissing(e.target.checked)} />
          Only names without audio
        </label>
        <div className="ml-auto text-sm text-[#63534E] font-mono">
          {filtered.filter((n) => n.audio_url).length} / {names.length} have audio
        </div>
      </div>
      <div className="space-y-3 max-h-[600px] overflow-y-auto pr-1">
        {filtered.map((n) => (
          <AudioUploadRow key={n.id} name={n} onUpdate={(updated) => setNames((prev) => prev.map((x) => x.id === updated.id ? updated : x))} />
        ))}
        {filtered.length === 0 && <div className="p-10 text-center text-[#63534E]">No names match this filter.</div>}
      </div>
    </div>
  );
}

const CSV_SAMPLE = `number,sanskrit,transliteration,hindi,english,meaning
109,ॐ नमस्ते अस्तु भगवन् विश्वेश्वराय नमः,Om Namaste Astu Bhagavan Vishveshvaraya Namah,विश्वेश्वर,Vishveshvara-Namaskara,Salutation to the Lord of the Universe
110,ॐ महादेवाय नमो नमः,Om Mahadevaya Namo Namah,महादेव,Mahadeva-Namostute,Salutation to the Great God`;

export function CsvImportTab() {
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = React.useRef();

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/names/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`Imported: ${data.inserted} new, ${data.updated} updated`);
    } catch (e) { toast.error(formatError(e)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const downloadSample = () => {
    const blob = new Blob([CSV_SAMPLE], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "pancham-dham-names-sample.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="csv-import-tab" className="max-w-3xl">
      <div className="bg-[#FFFDF9] sacred-border rounded-2xl p-7">
        <h3 className="font-serif text-2xl text-[#4A0E17]">Bulk-import names from CSV</h3>
        <p className="text-[#63534E] mt-3 text-sm leading-relaxed">
          Upload a CSV with columns: <code className="font-mono text-[#E65100]">number, sanskrit, transliteration, hindi, english, meaning</code>.
          Each row with a matching <code>number</code> updates the existing name; new numbers are inserted.
          Grow from 108 seeded names to the full 1008 in minutes.
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          <input ref={fileRef} data-testid="csv-file-input" type="file" accept=".csv,text/csv" onChange={(e) => upload(e.target.files?.[0])} className="hidden" />
          <Button data-testid="csv-upload-btn" disabled={uploading} onClick={() => fileRef.current?.click()} className="bg-[#4A0E17] hover:bg-[#36080F] rounded-full h-11 px-6">
            {uploading ? "Importing…" : <><FileSpreadsheet className="w-4 h-4 mr-2" /> Choose CSV file</>}
          </Button>
          <Button data-testid="csv-download-sample" variant="outline" onClick={downloadSample} className="rounded-full h-11 px-6 border-[#D4AF37] text-[#4A0E17] hover:bg-[#F3E5AB]">
            <Download className="w-4 h-4 mr-2" /> Download sample
          </Button>
        </div>
      </div>

      {result && (
        <div data-testid="csv-import-result" className="mt-6 bg-[#F3E5AB]/40 border border-[#D4AF37]/40 rounded-2xl p-6">
          <div className="font-serif text-xl text-[#4A0E17]">Import complete</div>
          <div className="mt-3 grid grid-cols-3 gap-4">
            <div><div className="overline">Inserted</div><div className="font-mono text-2xl text-[#4A0E17]">{result.inserted}</div></div>
            <div><div className="overline">Updated</div><div className="font-mono text-2xl text-[#4A0E17]">{result.updated}</div></div>
            <div><div className="overline">Total names</div><div className="font-mono text-2xl text-[#E65100]">{result.total_names}</div></div>
          </div>
          {result.errors?.length > 0 && (
            <details className="mt-4">
              <summary className="cursor-pointer text-sm font-semibold text-red-700">{result.errors.length} errors (click to see)</summary>
              <ul className="mt-2 text-sm text-red-700 space-y-1">
                {result.errors.map((e, i) => <li key={i}>• {e}</li>)}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

export function SevaClubMembersTab() {
  const [members, setMembers] = useState([]);
  useEffect(() => { api.get("/admin/seva-club/members").then((r) => setMembers(r.data.items)); }, []);

  return (
    <div data-testid="seva-club-members-tab">
      <div className="bg-[#FFFDF9] sacred-border rounded-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-[#D4AF37]/30">
          <div className="font-serif text-xl text-[#4A0E17]">Daily Blessing Club · {members.length} members</div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-[#4A0E17] text-[#F3E5AB]"><tr>
            <th className="text-left px-4 py-3">Name</th>
            <th className="text-left px-4 py-3">Email</th>
            <th className="text-left px-4 py-3">Gotra</th>
            <th className="text-left px-4 py-3">Joined</th>
            <th className="text-left px-4 py-3">Next renewal</th>
          </tr></thead>
          <tbody>
            {members.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-[#63534E]">No members yet</td></tr>}
            {members.map((m) => (
              <tr key={m.id} data-testid={`member-${m.email}`} className="border-t border-[#D4AF37]/20">
                <td className="px-4 py-3">{m.name || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs">{m.email}</td>
                <td className="px-4 py-3">{m.gotra || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs">{m.joined_at ? new Date(m.joined_at).toLocaleDateString() : "—"}</td>
                <td className="px-4 py-3 font-mono text-xs text-[#E65100]">{m.next_renewal || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
